import { Grid } from "@dextech-ai/dex-design-system";
import DSModal from "@/componentsDS/Modal/Modal.jsx";
import DSCardConfigure from "@/componentsDS/Modal/CardConfigure.jsx";
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import rem from "@/utils/rem.js";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import UseCaseAccordion from "./components/UseCaseAccordion";
import ManageLinksReview from "./components/ManageLinksReview";
import {
	seedFromDeLinks,
	toggleTriple,
	diffSelection,
	removalIds,
	buildAdditionsPayload,
	buildReviewModel,
	applyBuildexDefault,
	seedExpanded,
	parseTriple,
} from "./dsv-link-usecase-form";

function msgOf(err, fallback) {
	if (!err) return fallback;
	if (typeof err === "string") return err;
	return err.message || fallback;
}

/**
 * "Manage Use Cases" modal — a 2-step manage form:
 *  Step 1: accordion of ALL use cases (existing links pre-checked, per-persona
 *          role checkboxes; unchecking a role stages its removal).
 *  Step 2: full final-state review (added / unchanged / removed) → Confirm.
 * Additions are batched into one POST /usecasepersonas; removals go one-per-row
 * to /data-elements/persona/remove. On full success the modal closes; a partial
 * failure keeps it open with an error summary. `onApplied` fires either way so
 * the page always refetches the freshest link set.
 */
function LinkUseCasePersonaModal({ visible, hide, dataElement, deLinks, onApplied }) {
	const { linkUseCasePersona, removeDeLink, fetchUseCases, fetchPersonas } =
		useDataSchemaVersioning();

	const [step, setStep] = useState(1);
	const [useCases, setUseCases] = useState([]);
	const [personas, setPersonas] = useState([]);
	const [initialSelected, setInitialSelected] = useState({});
	const [edited, setEdited] = useState({});
	const [linkIdByTriple, setLinkIdByTriple] = useState({});
	const [expandedUseCaseIds, setExpandedUseCaseIds] = useState(() => new Set());
	const [personaFilterByUc, setPersonaFilterByUc] = useState({});
	const [bootstrapping, setBootstrapping] = useState(false);
	const [applying, setApplying] = useState(false);
	const [applyErrors, setApplyErrors] = useState([]);

	const prevVisibleRef = useRef(false);
	const deLinksRef = useRef(deLinks);
	deLinksRef.current = deLinks;

	// Seed only on the visible false→true transition, capturing deLinks at that
	// moment. Do NOT re-seed when the deLinks prop changes while open, otherwise a
	// post-Confirm page refetch would wipe un-applied staged edits.
	useEffect(() => {
		const rising = visible && !prevVisibleRef.current;
		prevVisibleRef.current = visible;
		if (!rising) return;

		let cancelled = false;
		setStep(1);
		setApplyErrors([]);
		setApplying(false);
		setBootstrapping(true);
		const seedLinks = deLinksRef.current;

		Promise.all([fetchUseCases(), fetchPersonas()])
			.then(([uc, p]) => {
				if (cancelled) return;
				const ucList = Array.isArray(uc) ? uc : [];
				const pList = Array.isArray(p) ? p : [];
				setUseCases(ucList);
				setPersonas(pList);

				const ucOptions = toUseCaseOptions(ucList);
				const pOptions = toPersonaOptions(pList);
				const { initialSelected: seeded, linkIdByTriple: idMap } =
					seedFromDeLinks(seedLinks);
				const edited0 = applyBuildexDefault({
					edited: seeded,
					initialSelected: seeded,
					useCaseOptions: ucOptions,
					personaOptions: pOptions,
				});
				setInitialSelected(seeded);
				setLinkIdByTriple(idMap);
				setEdited(edited0);
				setExpandedUseCaseIds(seedExpanded(ucOptions, edited0));
				setPersonaFilterByUc({});
			})
			.finally(() => {
				if (!cancelled) setBootstrapping(false);
			});
		return () => { cancelled = true; };
	}, [visible]);

	const useCaseOptions = useMemo(() => toUseCaseOptions(useCases), [useCases]);
	const personaOptions = useMemo(() => toPersonaOptions(personas), [personas]);

	const useCaseNameById = useMemo(() => {
		const map = {};
		for (const uc of useCaseOptions) map[uc.id] = uc.name;
		return map;
	}, [useCaseOptions]);

	const personaNameById = useMemo(() => {
		const map = {};
		for (const p of personaOptions) map[p.id] = p.name;
		return map;
	}, [personaOptions]);

	const { additionKeys, removalKeys } = useMemo(
		() => diffSelection(initialSelected, edited),
		[initialSelected, edited],
	);
	const isEmptyDiff = additionKeys.length === 0 && removalKeys.length === 0;

	const linkedCountByUc = useMemo(() => {
		const counts = {};
		for (const key of Object.keys(initialSelected)) {
			const { useCaseId } = parseTriple(key);
			counts[useCaseId] = (counts[useCaseId] || 0) + 1;
		}
		return counts;
	}, [initialSelected]);

	const reviewModel = useMemo(
		() =>
			buildReviewModel({ initialSelected, edited, useCaseNameById, personaNameById }),
		[initialSelected, edited, useCaseNameById, personaNameById],
	);

	function handleToggleExpand(ucId) {
		setExpandedUseCaseIds((prev) => {
			const next = new Set(prev);
			if (next.has(ucId)) next.delete(ucId);
			else next.add(ucId);
			return next;
		});
	}

	function handleToggleRole(ucId, personaId, roleValue) {
		setEdited((prev) => toggleTriple(prev, ucId, personaId, roleValue));
	}

	function handlePersonaFilterChange(ucId, value) {
		setPersonaFilterByUc((prev) => ({ ...prev, [ucId]: value }));
	}

	async function applyChanges() {
		if (isEmptyDiff || applying) return;
		setApplying(true);
		setApplyErrors([]);
		const errors = [];

		// 1) Additions first — a single batched, idempotent upsert POST.
		if (additionKeys.length > 0) {
			const payload = buildAdditionsPayload({
				additionKeys,
				useCaseNameById,
				dataElementId: dataElement?.dataElementId,
			});
			try {
				const res = await linkUseCasePersona(payload);
				if (res?.error) {
					errors.push(`Add links: ${msgOf(res.error, "Failed to add links")}`);
				}
			} catch (err) {
				errors.push(`Add links: ${msgOf(err, "Failed to add links")}`);
			}
		}

		// 2) Removals — independent; each can be blocked (e.g. link in use).
		const ids = removalIds(removalKeys, linkIdByTriple);
		const settled = await Promise.allSettled(ids.map((id) => removeDeLink(id)));
		settled.forEach((s) => {
			const r = s.status === "fulfilled" ? s.value : { error: "Failed to remove link" };
			if (r?.error) errors.push(`Remove: ${msgOf(r.error, "Failed to remove link")}`);
		});

		setApplying(false);
		if (typeof onApplied === "function") onApplied();
		if (errors.length === 0) {
			hide();
		} else {
			setApplyErrors(errors);
		}
	}

	if (!visible) return null;

	return (
		<DSModal
			visible={visible}
			close={hide}
			variant="large"
			addClass="dsv-ucp-modal"
			dismissClickOutsideModal={false}
		>
			<DSCardConfigure>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>
						{step === 1 ? "Manage Use Cases" : "Review Changes"}
					</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">&times;</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<p className="dsv-confirm__subtitle">
							Manage the use cases and personas <strong>{dataElement?.elementName || dataElement?.dataElementId}</strong> is linked to. Tick a role to add a link, untick it to remove.
						</p>
						<p className="dsv-ucp-modal__step">Step {step} of 2</p>

						<div className="dsv-ucp-modal__scroll">
							{bootstrapping ? (
								<p className="dsv-promote__hint">Loading use cases and personas…</p>
							) : step === 1 ? (
								<UseCaseAccordion
									useCaseOptions={useCaseOptions}
									personaOptions={personaOptions}
									edited={edited}
									expandedUseCaseIds={expandedUseCaseIds}
									personaFilterByUc={personaFilterByUc}
									linkedCountByUc={linkedCountByUc}
									onToggleExpand={handleToggleExpand}
									onToggleRole={handleToggleRole}
									onPersonaFilterChange={handlePersonaFilterChange}
								/>
							) : (
								<>
									<ManageLinksReview reviewModel={reviewModel} isEmptyDiff={isEmptyDiff} />
									{applyErrors.length > 0 && (
										<div className="dsv-ucp-review__errors" data-testid="ucp-apply-errors">
											{applyErrors.map((err, i) => (
												<p key={i} className="dsv-promote__hint" style={{ color: "#b45309" }}>
													{err}
												</p>
											))}
										</div>
									)}
								</>
							)}
						</div>
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<Grid autoFlow="column" autoColumns="max-content" columnGap={1.5} align="center">
							{step === 1 ? (
								<>
									<button
										className="dsv-btn dsv-btn--ghost"
										data-testid="ucp-cancel-btn"
										onClick={hide}
									>
										Cancel
									</button>
									<button
										className="dsv-btn dsv-btn--primary"
										data-testid="ucp-review-btn"
										onClick={() => setStep(2)}
										disabled={bootstrapping}
									>
										Review →
									</button>
								</>
							) : (
								<>
									<button
										className="dsv-btn dsv-btn--ghost"
										data-testid="ucp-back-btn"
										onClick={() => setStep(1)}
										disabled={applying}
									>
										← Back
									</button>
									<button
										className="dsv-btn dsv-btn--primary"
										data-testid="ucp-confirm-btn"
										onClick={applyChanges}
										disabled={isEmptyDiff || applying}
									>
										{applying ? "Saving…" : "Confirm"}
									</button>
								</>
							)}
						</Grid>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

function toUseCaseOptions(useCases) {
	return (useCases || [])
		.map((uc) => ({
			id: uc.useCaseId ?? uc.id,
			name: uc.name ?? uc.useCaseName ?? uc.useCaseId,
		}))
		.filter((u) => u.id);
}

function toPersonaOptions(personas) {
	// GET /personas can return the same persona id more than once (the backend
	// joins UseCasePersona and projects the per-use-case is_etr_issuer flag,
	// which differs across use cases and defeats DISTINCT). Dedupe by id, keeping
	// the first occurrence, so each persona renders once and its React key stays
	// unique.
	const seen = new Set();
	return (personas || [])
		.map((p) => ({
			id: p.personaId ?? p.id,
			name: p.name ?? p.personaName ?? p.personaId,
		}))
		.filter((p) => p.id)
		.filter((p) => {
			if (seen.has(p.id)) return false;
			seen.add(p.id);
			return true;
		});
}

LinkUseCasePersonaModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	dataElement: PropTypes.shape({
		dataElementId: PropTypes.string,
		elementName: PropTypes.string,
	}),
	deLinks: PropTypes.arrayOf(PropTypes.object),
	onApplied: PropTypes.func,
};

LinkUseCasePersonaModal.defaultProps = {
	deLinks: [],
};

export default LinkUseCasePersonaModal;
